import {
  HistogramSeries,
  IChartApi,
  ISeriesApi,
  LineSeries
} from 'lightweight-charts';
import { useCallback, useEffect, useMemo } from 'react';
import { IndicatorType } from '@shared/types';
import { useChartContext } from '../components/chart.context';
import { useSubplotSeries } from './use-subplot-series';
import {
  isMacdIndicatorUpdate,
  MacdIndicatorUpdate
} from '../services/indicator-update.service';
import { useIndicatorUpdateStream } from './use-indicator-update-stream.hook';

type MacdSeriesGroup = {
  chart: IChartApi;
  macd: ISeriesApi<'Line'>;
  signal: ISeriesApi<'Line'>;
  histogram: ISeriesApi<'Histogram'>;
};

const POSITIVE_HISTOGRAM_COLOR = '#26a69a';
const NEGATIVE_HISTOGRAM_COLOR = '#ef5350';

const toLineData = (
  points: MacdIndicatorUpdate['data'],
  valueSelector: (
    point: MacdIndicatorUpdate['data'][number]
  ) => number | undefined
) =>
  // Keep full timestamp grid via whitespace points to preserve logical alignment with other panes.
  points.map((point) => {
    const value = valueSelector(point);
    return typeof value === 'number'
      ? { time: point.time, value }
      : { time: point.time };
  });

const toHistogramData = (points: MacdIndicatorUpdate['data']) =>
  // Histogram follows the same timeline rule: missing value still emits whitespace.
  points.map((point) => {
    if (typeof point.histogram !== 'number') {
      return { time: point.time };
    }

    return {
      time: point.time,
      value: point.histogram,
      color:
        point.histogram >= 0
          ? POSITIVE_HISTOGRAM_COLOR
          : NEGATIVE_HISTOGRAM_COLOR
    };
  });

export const useMacdSeries = () => {
  const {
    chartInitialized,
    indicatorSeriesRefs,
    data: { indicatorConfigs }
  } = useChartContext();

  const { initializeChartAndSeriesArray } = useSubplotSeries();

  const hasMacd = useMemo(
    () =>
      indicatorConfigs.subplot.some(
        (indicator) => indicator.type === IndicatorType.MACD
      ),
    [indicatorConfigs.subplot]
  );

  const isLastIndicator = useMemo(
    () => indicatorConfigs.subplot.at(-1)?.type === IndicatorType.MACD,
    [indicatorConfigs.subplot]
  );

  const getOrCreateSeriesGroup = useCallback(
    (indicatorIndex: number): MacdSeriesGroup | null => {
      const existing = indicatorSeriesRefs.current[IndicatorType.MACD]?.[
        indicatorIndex
      ] as MacdSeriesGroup | undefined;
      if (existing) return existing;

      const { chart, seriesArray } = initializeChartAndSeriesArray(
        IndicatorType.MACD,
        isLastIndicator
      );

      const histogram = chart.addSeries(HistogramSeries, {
        priceScaleId: 'right',
        priceLineVisible: false,
        lastValueVisible: false
      });

      const macd = chart.addSeries(LineSeries, {
        color: 'blue',
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: false,
        priceScaleId: 'right'
      });

      const signal = chart.addSeries(LineSeries, {
        color: 'orange',
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: false,
        priceScaleId: 'right'
      });

      const group: MacdSeriesGroup = {
        chart,
        macd,
        signal,
        histogram
      };

      seriesArray[indicatorIndex] = group;
      return group;
    },
    [initializeChartAndSeriesArray, isLastIndicator]
  );

  const applyUpdate = useCallback(
    (update: MacdIndicatorUpdate) => {
      const seriesGroup = getOrCreateSeriesGroup(update.indicatorIndex);
      if (!seriesGroup) return;

      if (update.updateType === 'snapshot') {
        // Snapshot replaces full MACD state for this indicator index.
        seriesGroup.macd.setData(
          toLineData(update.data, (point) => point.macd)
        );
        seriesGroup.signal.setData(
          toLineData(update.data, (point) => point.signal)
        );
        seriesGroup.histogram.setData(toHistogramData(update.data));
        seriesGroup.chart.timeScale().fitContent();
        return;
      }

      update.data.forEach((point) => {
        // Delta updates single bars and may intentionally mark missing values as whitespace.
        seriesGroup.macd.update(
          typeof point.macd === 'number'
            ? { time: point.time, value: point.macd }
            : { time: point.time }
        );

        seriesGroup.signal.update(
          typeof point.signal === 'number'
            ? { time: point.time, value: point.signal }
            : { time: point.time }
        );

        seriesGroup.histogram.update(
          typeof point.histogram === 'number'
            ? {
                time: point.time,
                value: point.histogram,
                color:
                  point.histogram >= 0
                    ? POSITIVE_HISTOGRAM_COLOR
                    : NEGATIVE_HISTOGRAM_COLOR
              }
            : { time: point.time }
        );
      });
    },
    [getOrCreateSeriesGroup]
  );

  useIndicatorUpdateStream({
    chartInitialized,
    enabled: hasMacd,
    indicatorType: IndicatorType.MACD,
    isExpectedUpdate: isMacdIndicatorUpdate,
    applyUpdate
  });
};
